<template>
  <div class="w-[900px] max-w-full">
    <div class="relative">
      <PreviewCanvas
        ref="canvasRef"
        class="h-[640px]"
        mode="preview"
        :url="url"
        :token="token"
        :scene="initialScene"
        @scene-changes="changes = $event"
      />
      <!-- Covers the canvas too, so the photo cannot be moved while it is being saved. -->
      <Loading v-if="isSaving" class="absolute inset-0 bg-white/70">
        <p class="text-base text-gray-800">Saving preview...</p>
      </Loading>
    </div>
    <div
      v-if="editable && hasUnsavedChanges"
      data-test="save-scene-bar"
      class="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-hqPurple300 bg-[#fbf7f8] px-5 py-3"
    >
      <div class="min-w-0">
        <p class="font-medium text-gray-900">{{ changesSummary }}</p>
        <p class="text-sm text-gray-500">
          Save the new position to update this preview and its share link.
        </p>
      </div>
      <Button
        data-test="save-scene"
        primaryButton
        leftIcon="check"
        leftIconColor="white"
        :text="isSaving ? 'Saving...' : 'Save position'"
        :disabled="isSaving"
        @click="save"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { updatePrettyPreviewApi } from "@/api/previewRequests";
import Button from "@/components/UI/Form/Button/Button.vue";
import Loading from "@/components/UI/Loading/Loading.vue";
import { useModal } from "@/components/UI/Modal/useModal";
import PreviewCanvas from "@/components/Previews/PreviewCanvas/PreviewCanvas.vue";
import useToast from "@/hooks/useToast";
import { parseScene } from "@/utils/parseScene";
import { describeSceneChanges } from "@/utils/sceneChanges";

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  url: {
    type: String,
    required: true,
  },
  token: {
    type: String,
    default: "",
  },
  scene: {
    type: String,
    default: "",
  },
  // Only a preview no order was created from yet may be changed.
  editable: {
    type: Boolean,
    default: false,
  },
  // Called with the saved scene once it is stored, e.g. so the list reloads its rows.
  onSaved: {
    type: Function,
    default: () => {},
  },
});

const { openToast } = useToast();
const { close } = useModal();

const canvasRef = ref(null);
const initialScene = parseScene(props.scene);
const isSaving = ref(false);
// What the user changed in the canvas, as the canvas component worked it out.
const changes = ref([]);
const hasUnsavedChanges = computed(() => changes.value.length > 0);
const changesSummary = computed(() => describeSceneChanges(changes.value));

const save = async () => {
  isSaving.value = true;

  let captured;
  try {
    captured = await canvasRef.value.capture();
  } catch (error) {
    openToast("Failed to capture the preview from the canvas. Please try again.", "danger");
    isSaving.value = false;
    return;
  }

  const canvasScene = captured.scene ? JSON.stringify(captured.scene) : "";
  try {
    const { data } = await updatePrettyPreviewApi(props.id, {
      canvas_scene: canvasScene,
      preview_image: captured.screenshot,
    });
    props.onSaved({ canvas_scene: canvasScene, ...(data?.prettyPreview || {}) });
    openToast("Preview saved", "success");
    close();
  } catch (error) {
    // The request layer has already toasted the server's message.
  } finally {
    isSaving.value = false;
  }
};
</script>
